import { refreshBookings } from "../../../../utils/apis/refreshBookings";
import { BookingsData, editMultiBooking, editSingleBooking, updateValetBooking } from "../../../../utils/apis/bookings_repository";
import { Text } from "../../Text";
import { DatePicker } from "../../inputs/DatePicker";
import { getDateObject, getTime, getDateTimeString, formatDateTime, prettifyTime } from "../../../../utils/helper";
import { useState } from "react";
import { Button } from "../../CustomButton";
import { TimePicker } from "../../inputs/TimePicker";
import { DatePickerWithRange } from "../../inputs/DateRangePicker";
import { SheetClose } from "../../Sheets";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { showToast } from "../../../../utils/toast";
import { DateTime } from "luxon";
import { changeBooking, changeSeries, changeSeriesDay, madeByService, seriesNeedsService } from "../../../../utils/apis/series_days";
import { bookingDays } from "../../../../utils/apis/ev_bookings";
import { getTenant } from "../../../../utils/apis/tenants_repository";
import { describeBooking } from "../../chips/BookingTypeChip";
import { moveWithArrows, Notice, ParkingCard, ParkingSpace, Segmented } from "../bookingForm";
import { notesFrom, spacesLine, useMonthSpaces } from "../bookingSpaces";
import { spacesFor } from "../../../../utils/apis/availability";
import { getUser } from "../../../../utils/apis/user_repository";
import { askBookingService } from "../../../../utils/apis/booking_service";


interface EditBookingBodyProps {
    booking: Partial<BookingsData>;
    bookingId: string
    setSheetState: (state: boolean) => void;
}

const EditBookingBody = ({ booking, bookingId, setSheetState }: EditBookingBodyProps) => {

    const queryClient = useQueryClient();

    const [bookingType, setBookingType] = useState(booking.bookingType);
    const [startTime, updateStartTime] = useState<string>(getTime(booking.startTime!));
    const [endTime, updateEndTime] = useState<string>(getTime(booking.endTime!));

    const [isLoading, setIsLoading] = useState(false);
    const [retailUserName, setRetailUserName] = useState(booking.name);
    const [retailUserEmail, setRetailUserEmail] = useState(booking.email);
    const [retailUserPhone, setRetailUserPhone] = useState(booking.phone);
    const [retailUserVenue, setRetailUserVenue] = useState(booking.venue);
    const [retailUserPlate, setRetailUserPlate] = useState(booking.plate);
    const [startDate, setStartDate] = useState<Date>(getDateObject(booking.startTime!));
    const [endDate, setEndDate] = useState<Date>(
        booking.bookingType === "Single" ? getDateObject(booking.endTime!) : getDateObject(booking.originalBooking!.endTime)
    );

    const [shownMonth, setShownMonth] = useState<Date>(getDateObject(booking.startTime!));
    const originalSpace: ParkingSpace = describeBooking(booking as BookingsData).space === "EV" ? "EV" : "Standard";
    const [space, setSpace] = useState<ParkingSpace>(originalSpace);

    const handleClickMulti = () => {
        setBookingType("Multi");
        // A day of a series the service made: start from the whole series, not just this day. A series whose
        // days end at midnight records its end as midnight after its last day.
        if (madeByService(booking) && booking.originalBooking) {
            const end = booking.originalBooking.endTime;
            setStartDate(getDateObject(booking.originalBooking.startTime));
            setShownMonth(getDateObject(booking.originalBooking.startTime));
            setEndDate(DateTime.fromJSDate(getDateObject(end)).minus({ days: end.endsWith("T00:00:00.000Z") ? 1 : 0 }).toJSDate());
        }
    }

    const handleClickSingle = () => {
        setBookingType("Single");
        // A day of a series keeps the series' parking type.
        setSpace(originalSpace);
        // Back to this day, not the series' first one.
        if (madeByService(booking)) {
            setStartDate(getDateObject(booking.startTime!));
            setEndDate(getDateObject(booking.endTime!));
            setShownMonth(getDateObject(booking.startTime!));
        }
    }

    const handleStartTime = (time: string) => {
        updateStartTime(time);
    }

    const handleEndTime = (time: string): void => {
        updateEndTime(time);
    }

    const handleStartDate = (date: Date) => {
        setStartDate(date);
        setShownMonth(date);
    }

    const handleEndDate = (date: Date) => {
        setEndDate(date);
    }


    const singleBookingMutation = useMutation({
        mutationFn: editSingleBooking,
        onSuccess: (val) => {
            console.log("Loading state", isLoading)
            if (val) {
                console.log("<<== single booking updated successfully ==>>");
                refreshBookings(queryClient);
                setSheetState(false);
            } else {
                setTimeout(() => {
                    console.log("<<== booking update failed ==>>");
                    refreshBookings(queryClient);
                    setSheetState(false);
                }, 2000);
            }
        },
    })

    const multiBookingMutation = useMutation({
        mutationFn: editMultiBooking,
        onSuccess: () => {
            console.log("<<== multi booking updated successfully ==>>");
            refreshBookings(queryClient);
            setSheetState(false);
        },
    })

    // Series days at a switched-over company change through the booking service.
    const serviceMutation = useMutation({
        mutationFn: (save: () => Promise<boolean>) => save(),
        onSuccess: (saved) => {
            if (saved) {
                refreshBookings(queryClient);
                setSheetState(false);
            }
            setIsLoading(false);
        },
        onError: () => {
            showToast("We couldn't save this change just now. Please try again.", { type: "error" });
            setIsLoading(false);
        },
    })

    const handleBookingUpdate = async () => {

        const now = DateTime.now();

        if ((!startDate || !endDate) && bookingType === "Multi") {
            showToast("One or more date fields are empty", {
                type: "warning"
            });
            return;
        }

        if (!startDate && bookingType === "Single") {
            showToast("Start date is empty", {
                type: "warning"
            });
            return;
        }

        if (!startTime) {
            showToast("Start time is empty", {
                type: "warning"
            });
            return;
        }

        if (!endTime) {
            showToast("End time is empty", {
                type: "warning"
            });
            return;
        }


        const updatedStartDate = getDateTimeString(startDate.toISOString(), startTime);
        const updatedEndDate = getDateTimeString(bookingType === "Single" ? startDate.toISOString() : endDate.toISOString(), endTime);

        if(DateTime.fromJSDate(new Date(updatedStartDate)) < now) {
            showToast("Start time cannot be in the past", {
                type: "warning"
            });
            return;
        }

        if(DateTime.fromJSDate(new Date(updatedStartDate)) > DateTime.fromJSDate(new Date(updatedEndDate))) {
            showToast("Start date cannot be after than the end date", {
                type: "warning"
            });
            return;
        }

        setIsLoading(true);

        // One day of any series, or the whole of a series the service made, once the company has switched over.
        if ((bookingType === "Single" && booking.multiID) || (bookingType === "Multi" && madeByService(booking))) {
            let viaService = false;
            try {
                viaService = await seriesNeedsService(booking);
            } catch (error) {
                showToast("We couldn't reach the booking service just now. Please try again.", { type: "error" });
                setIsLoading(false);
                return;
            }
            if (viaService) {
                serviceMutation.mutate(bookingType === "Single" ?
                    () => changeSeriesDay(booking, { startTime: formatDateTime(updatedStartDate), endTime: formatDateTime(updatedEndDate) }) :
                    async () => changeSeries(booking, bookingDays(startDate, endDate, startTime, endTime,
                        Boolean((await getTenant(booking.tenant!))?.isWeekend)), { parkingSpaceType: spaceType }));
                return;
            }
        }

        // Only the service can move a booking to the other parking type.
        if (bookingType === "Single" && !booking.multiID && space !== originalSpace) {
            serviceMutation.mutate(() => changeBooking(booking,
                { startTime: formatDateTime(updatedStartDate), endTime: formatDateTime(updatedEndDate) }, spaceType));
            return;
        }

        if(bookingType === "Single"){
            const updatedBooking: Partial<BookingsData> = {
                bookingId: bookingId,
                startTime: updatedStartDate,
                endTime: updatedEndDate,
            }
            singleBookingMutation.mutate(updatedBooking);
        }else if(bookingType === "Multi"){
            const updatedBooking: Partial<BookingsData> = {
                multiID: booking.multiID,
                startTime: updatedStartDate,
                endTime: updatedEndDate,
            }
            multiBookingMutation.mutate(updatedBooking);
        }else if(bookingType === "Guest"){

        const bookingData = {
            startTime: startDate.toISOString(),
            venue: retailUserVenue,
            email: retailUserEmail,
            plateNumber: retailUserPlate!.toString(),
            bookingId: bookingId,
            phone: retailUserPhone,
            name: retailUserName,
        };

        try {
            const success = await updateValetBooking(bookingData);
            if (success) {
                setIsLoading(true); // Set edit success state
                refreshBookings(queryClient).then(() => {
                    setSheetState(false);
                });
            }
            // setSavingChanges(false);
        } catch (error) {
            console.error('Error saving booking changes:', error);
            // setSavingChanges(false);
        }
        }
    }
    const handleDateChange = async (date: Date) => {
        setStartDate(date);

    };

    const spaceType = space === "EV" ? "ev" : "standard";
    // A series can be changed a day at a time or as a whole.
    const inSeries = Boolean(booking.multiID) || booking.bookingType === "Multi";
    const plates = (booking.plate ?? []).filter((plate) => typeof plate === "string" && plate.trim());
    // Free spaces at the person's company, leaving this booking (or its whole series) out, as the app's edit
    // calendar does. Companies that haven't switched over don't answer, so nothing is shown for them.
    const editingSeat = bookingType === "Single" || bookingType === "Multi";
    const { data: person } = useQuery({
        queryKey: ['user', booking.userId],
        queryFn: () => getUser(booking.userId!),
        enabled: editingSeat && Boolean(booking.userId),
        staleTime: 60_000,
    });
    const { data: company } = useQuery({
        queryKey: ['tenantData', booking.tenant],
        queryFn: () => getTenant(booking.tenant!),
        enabled: editingSeat && Boolean(booking.tenant),
        staleTime: 60_000,
    });
    const spacesPerson = person?.capacityTenantId ? person : undefined;
    // Once the company has switched over, a booking on its own or a whole series the service made can move to the
    // other parking type; a day of a series keeps the series' type. EV is offered while the company takes EV
    // bookings, and an EV booking can always move off it (also while EV is paused).
    const { data: service } = useQuery({
        queryKey: ['bookingService', spacesPerson?.userId, spacesPerson?.capacityTenantId],
        queryFn: () => askBookingService(spacesPerson!),
        enabled: Boolean(spacesPerson),
        staleTime: 60_000,
    });
    const seriesDay = bookingType === "Single" && Boolean(booking.multiID);
    const typeChangeable = Boolean(service?.switched) && (bookingType === "Single" ? !seriesDay : madeByService(booking));
    const evOffered = Boolean(service?.evEnabled) || originalSpace === "EV";
    const editing = booking.bookingId ? { bookingId: booking.bookingId, wholeSeries: bookingType === "Multi" } : undefined;
    const hoursValid = bookingDays(new Date(2030, 0, 7), new Date(2030, 0, 7), startTime, endTime, true).every((day) => day.endTime > day.startTime);
    // The calendar shows each day's spaces over the company's full day, as in Create Booking; the card and notices
    // count at the chosen hours.
    const fullDayStart = prettifyTime(company?.fulldayDefaultTimes?.startHour || 800);
    const fullDayEnd = prettifyTime(company?.fulldayDefaultTimes?.endHour || 1800);
    const shownMonths = bookingType === "Single" ? [shownMonth] : [shownMonth, DateTime.fromJSDate(shownMonth).plus({ months: 1 }).toJSDate()];
    const monthSpaces = [
        useMonthSpaces(spacesPerson, shownMonths[0], fullDayStart, fullDayEnd, Boolean(company), editing),
        useMonthSpaces(spacesPerson, shownMonths[1], fullDayStart, fullDayEnd, Boolean(company), editing),
    ];
    const dayNotes = notesFrom(monthSpaces, space);
    const checkedDays = !hoursValid ? [] : bookingType === "Single" ? bookingDays(startDate, startDate, startTime, endTime, true) :
        endDate && company ? bookingDays(startDate, endDate, startTime, endTime, Boolean(company.isWeekend)) : [];
    const { data: chosenDays, isFetching: checkingSpaces, isError: spacesUnknown } = useQuery({
        queryKey: ['spaces', spacesPerson?.capacityTenantId, booking.userId, 'editing', editing?.bookingId, editing?.wholeSeries, JSON.stringify(checkedDays)],
        queryFn: () => spacesFor(spacesPerson!, checkedDays, { editing }),
        enabled: Boolean(spacesPerson && checkedDays.length),
        staleTime: 60_000,
    });
    const chosenSpaces = chosenDays?.[space === "EV" ? "ev" : "standard"];
    // EV needs a free bay; a Standard change can still go ahead and the car park decides, as in Create Booking.
    const noEvSpace = space === "EV" && chosenSpaces?.available === 0;
    const spacesText = (type: ParkingSpace) => spacesPerson && hoursValid ? spacesLine(chosenDays, type, checkingSpaces, spacesUnknown) : undefined;
    const calendarCaption = spacesPerson && company ? `${space} availability · ${fullDayStart} – ${fullDayEnd} (full day) · Sydney time` : undefined;

    const dateText = bookingType === "Single" ? DateTime.fromJSDate(startDate).toFormat("d LLL yyyy") :
        `${DateTime.fromJSDate(startDate).toFormat("d LLL yyyy")} – ${endDate ? DateTime.fromJSDate(endDate).toFormat("d LLL yyyy") : "End date"}`;
    return (
        <>
                     {bookingType === "Guest" && (

              <><div className="scrollable-content mt-1" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Text as="h3">Booking Type</Text>
                    <div className="flex flex-row space-x-4 mt-6">
                        <Button onClick={handleClickSingle} variant={bookingType === "Guest" ? "default" : "secondary"} className={`w-44 border-primary ${bookingType === "Guest" ? "text-white" : "border-primary text-primaryText"}`}>Valet Booking</Button>
                    </div>

                    <Text className="mt-10 mb-5" as="h3">Booking Date</Text>
                    <DatePicker setNewDate={(value) => handleDateChange(value)} initialDate={startDate} />
                    <Text as="h3">Retail User Details</Text>
                    <div className="flex flex-col gap-4 mt-6">
                        <input
                            className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
                            type="text"
                            placeholder="Name*"
                            value={retailUserName}
                            onChange={(e) => setRetailUserName(e.target.value)}
                            required />
                        <input
                            className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
                            type="text"
                            placeholder="Email*"
                            value={retailUserEmail}
                            onChange={(e) => setRetailUserEmail(e.target.value)}
                            required />
                        <input
                            className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
                            type="text"
                            placeholder="Phone*"
                            value={retailUserPhone}
                            onChange={(e) => setRetailUserPhone(e.target.value)}
                            required />
                        <input
                            className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
                            type="text"
                            placeholder="Venue*"
                            value={retailUserVenue}
                            onChange={(e) => setRetailUserVenue(e.target.value)}
                            disabled />
                        <input
                            className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
                            type="text"
                            placeholder="Plate"
                            value={retailUserPlate}
                            onChange={(e) => setRetailUserPlate([e.target.value.toUpperCase()])} />
                    </div>

                </div><div className="absolute bottom-0 mx-12 right-0 mb-20 flex flex-row justify-end">
                        <SheetClose asChild>
                            <Button variant={"secondary"} className="w-44 border border-primary text-primaryText">Cancel</Button>
                        </SheetClose>
                        <Button variant={"default"} isLoading={isLoading} onClick={handleBookingUpdate} className="w-44 ml-4 text-white">Save Changes</Button>
                    </div></>
            )}
            {bookingType === "Multi" || bookingType === "Single" ? (
                // Fills the sheet below its header and pins the footer, as Create Booking does.
                <div className="flex min-h-0 flex-1 flex-col text-left">
                    <div className="flex-1 overflow-y-auto pr-1 pb-6">
                        <p className="mt-6 text-sm text-app-secondary">Change the date or hours of {booking.name ? `${booking.name}'s booking` : "this booking"}.</p>

                        <section className="mt-6">
                            <h3 className="mb-3 text-lg font-bold text-app-text">Parking type</h3>
                            {typeChangeable && evOffered ? (
                                <div role="radiogroup" aria-label="Parking type" className="flex gap-3"
                                    onKeyDown={(event) => moveWithArrows(event, ["Standard", "EV"] as ParkingSpace[], space, setSpace)}>
                                    <ParkingCard space="Standard" selected={space === "Standard"} onSelect={() => setSpace("Standard")} detail={spacesText("Standard")} />
                                    <ParkingCard space="EV" selected={space === "EV"} onSelect={() => setSpace("EV")} detail={spacesText("EV")} />
                                </div>
                            ) : (
                                <div className="flex gap-3">
                                    <ParkingCard space={space} selected detail={spacesText(space)} />
                                </div>
                            )}
                            {seriesDay && service?.switched && evOffered ?
                                <p className="mt-2 text-xs text-app-secondary">This day keeps its series' parking type. To change it, choose Whole series.</p> :
                                !typeChangeable && <p className="mt-2 text-xs text-app-secondary">To change the parking type, cancel this booking and create a new one.</p>}
                        </section>

                        <section className="mt-6 space-y-4 rounded-[20px] border border-app-border bg-white p-5">
                            <h3 className="text-lg font-bold text-app-text">When are you parking?</h3>
                            {inSeries && <Segmented label="Change" value={bookingType}
                                options={[{ value: "Single", label: "This day" }, { value: "Multi", label: "Whole series" }]}
                                onChange={(value) => value === "Single" ? handleClickSingle() : handleClickMulti()} />}
                            <div>
                                <p className="mb-2 text-sm font-semibold text-app-text">{bookingType === "Single" ? "Date" : "Dates"}</p>
                                {bookingType === "Single" ?
                                    <DatePicker setNewDate={(value) => handleStartDate(value)} initialDate={startDate} dayNotes={dayNotes} caption={calendarCaption} onMonthChange={setShownMonth} month={shownMonth} /> :
                                    <DatePickerWithRange setStartDate={(val) => handleStartDate(val)} setEndDate={(val) => handleEndDate(val)} startDate={startDate} endDate={endDate}
                                        dayNotes={dayNotes} caption={calendarCaption} onMonthChange={setShownMonth} month={shownMonth} />}
                                {spacesPerson && <p className="mt-2 text-xs text-app-secondary">See {space} availability in the calendar. This booking's own space counts as free.</p>}
                            </div>
                            <div className="flex gap-6">
                                <div>
                                    <p className="mb-2 text-sm font-semibold text-app-text">Start time</p>
                                    <TimePicker initialTime={startTime} onChange={handleStartTime} />
                                </div>
                                <div>
                                    <p className="mb-2 text-sm font-semibold text-app-text">End time</p>
                                    <TimePicker initialTime={endTime} onChange={handleEndTime} />
                                </div>
                            </div>
                            <p className="text-xs text-app-secondary">All times are Sydney time.</p>
                            {spacesPerson && !hoursValid && <Notice>Choose valid hours to check availability.</Notice>}
                            {chosenSpaces && (chosenSpaces.available > 0 ?
                                <Notice tone="success">{space} parking is available.</Notice> :
                                <Notice>No {space} spaces for these dates and hours. Choose another date.</Notice>)}
                            {spacesUnknown && <Notice>{space === "EV" ? "Availability couldn't be checked. Please try again in a moment." :
                                "Availability couldn't be checked. You can still save this change."}</Notice>}
                        </section>

                        <section className="mt-6">
                            <h3 className="mb-3 text-lg font-bold text-app-text">Registered vehicles</h3>
                            {plates.length ? (
                                <div className="flex flex-wrap gap-2">
                                    {plates.map((plate) => <span key={plate} className="rounded-lg bg-app-muted px-3 py-2 text-sm font-semibold text-app-text">{plate}</span>)}
                                </div>
                            ) : (
                                <Notice>There's no vehicle on this booking, so their access card is used instead.</Notice>
                            )}
                        </section>
                    </div>

                    {/* Summary and actions, as at the foot of Create Booking */}
                    <div className="border-t border-app-border pt-4 pb-6">
                        <p className="text-sm text-app-text">{space} · {dateText}{booking.name ? ` · ${booking.name}` : ""}</p>
                        <p className="text-sm text-app-secondary">{startTime} – {endTime}</p>
                        <div className="mt-4 flex gap-3">
                            <SheetClose asChild>
                                <Button variant={"secondary"} className="flex-1 border border-primary text-primaryText">Cancel</Button>
                            </SheetClose>
                            <Button variant={"default"} isLoading={isLoading} onClick={handleBookingUpdate} disabled={noEvSpace} className="flex-[2] text-white disabled:opacity-50">Save changes</Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}

export default EditBookingBody;

// import { BookingsData, editMultiBooking, editSingleBooking } from "../../../../utils/apis/bookings_repository";
// import { Text } from "../../Text";
// import { DatePicker } from "../../inputs/DatePicker";
// import { getDateObject, getTime, addDays, getDateTimeString } from "../../../../utils/helper";
// import { useEffect, useState } from "react";
// import { Button } from "../../CustomButton";
// import { TimePicker } from "../../inputs/TimePicker";
// import { DatePickerWithRange } from "../../inputs/DateRangePicker";
// import { SheetClose } from "../../Sheets";
// import { useMutation, useQueryClient } from "@tanstack/react-query";
// import { showToast } from "../../../../utils/toast";
// import { DateTime } from "luxon";

// interface EditBookingBodyProps {
//     booking: Partial<BookingsData>;
//     bookingId: string;
//     setSheetState: (state: boolean) => void;
// }

// const EditBookingBody = ({ booking, bookingId, setSheetState }: EditBookingBodyProps) => {
//     const queryClient = useQueryClient();

//     const [bookingType, setBookingType] = useState(booking.bookingType);
//     const [startTime, updateStartTime] = useState<string>(getTime(booking.startTime!));
//     const [endTime, updateEndTime] = useState<string>(getTime(booking.endTime!));
//     const [startDate, setStartDate] = useState<Date>(getDateObject(booking.startTime!));
//     const [endDate, setEndDate] = useState<Date>(getDateObject(booking.endTime!));
//     const [retailUserName, setRetailUserName] = useState(booking.name);
//     const [retailUserEmail, setRetailUserEmail] = useState(booking.email);
//     const [retailUserPhone, setRetailUserPhone] = useState(booking.phone);
//     const [retailUserPlate, setRetailUserPlate] = useState(booking.plate);

//     const [isLoading, setIsLoading] = useState(false);
//     const handleClickMulti = () => {
//         setBookingType("Multi");
//     }

//     const handleClickSingle = () => {
//         setBookingType("Single");
//     }

//     const handleStartTime = (time: string) => {
//         updateStartTime(time);
//     }

//     const handleEndTime = (time: string): void => {
//         updateEndTime(time);
//     }

//     const handleStartDate = (date: Date) => {
//         setStartDate(date);
//     }

//     const handleEndDate = (date: Date) => {
//         setEndDate(date);
//     }


//     const singleBookingMutation = useMutation({
//         mutationFn: editSingleBooking,
//         onSuccess: (val) => {
//             console.log("Loading state", isLoading)
//             if (val) {
//                 console.log("<<== single booking updated successfully ==>>");
//                 queryClient.invalidateQueries({ queryKey: ['bookings'] });
//                 setSheetState(false);
//             } else {
//                 setTimeout(() => {
//                     console.log("<<== booking update failed ==>>");
//                     queryClient.invalidateQueries({ queryKey: ['bookings'] });
//                     setSheetState(false);
//                 }, 2000);
//             }
//         },
//     })

//     const multiBookingMutation = useMutation({
//         mutationFn: editMultiBooking,
//         onSuccess: () => {
//             console.log("<<== multi booking updated successfully ==>>");
//             queryClient.invalidateQueries({ queryKey: ['bookings'] });
//             setSheetState(false);
//         },
//     })

//     const handleBookingUpdate = async () => {

//         const now = DateTime.now();

//         if ((!startDate || !endDate) && bookingType === "Multi") {
//             showToast("One or more date fields are empty", {
//                 type: "warning"
//             });
//             return;
//         }

//         if (!startDate && bookingType === "Single") {
//             showToast("Start date is empty", {
//                 type: "warning"
//             });
//             return;
//         }

//         if (!startTime) {
//             showToast("Start time is empty", {
//                 type: "warning"
//             });
//             return;
//         }

//         if (!endTime) {
//             showToast("End time is empty", {
//                 type: "warning"
//             });
//             return;
//         }


//         const updatedStartDate = getDateTimeString(startDate.toISOString(), startTime);
//         const updatedEndDate = getDateTimeString(bookingType === "Single" ? startDate.toISOString() : endDate.toISOString(), endTime);

//         if(DateTime.fromJSDate(new Date(updatedStartDate)) < now) {
//             showToast("Start time cannot be in the past", {
//                 type: "warning"
//             });
//             return;
//         }

//         if(DateTime.fromJSDate(new Date(updatedStartDate)) > DateTime.fromJSDate(new Date(updatedEndDate))) {
//             showToast("Start date cannot be after than the end date", {
//                 type: "warning"
//             });
//             return;
//         }

//         setIsLoading(true);

//         if(bookingType === "Single"){
//             const updatedBooking: Partial<BookingsData> = {
//                 bookingId: bookingId,
//                 startTime: updatedStartDate,
//                 endTime: updatedEndDate,
//             }
//             singleBookingMutation.mutate(updatedBooking);
//         }else if(bookingType === "Multi"){
//             const updatedBooking: Partial<BookingsData> = {
//                 multiID: booking.multiID,
//                 startTime: updatedStartDate,
//                 endTime: updatedEndDate,
//             }
//             multiBookingMutation.mutate(updatedBooking);
//         }
//     }
//     useEffect(() => {
// console.log("bboking ", booking)
//     })
//     const singleBookingMutation = useMutation({
//         mutationFn: editSingleBooking,
//         onSuccess: () => {
//             queryClient.invalidateQueries({ queryKey: ["bookings"] });
//             setSheetState(false);
//         },
//     });

//     const multiBookingMutation = useMutation({
//         mutationFn: editMultiBooking,
//         onSuccess: () => {
//             queryClient.invalidateQueries({ queryKey: ["bookings"] });
//             setSheetState(false);
//         },
//     });

//     const handleBookingUpdate = async () => {
//         if (!startDate || !startTime || !endTime) {
//             showToast("Please fill in all required fields.", { type: "warning" });
//             return;
//         }

//         const updatedStartDate = getDateTimeString(startDate.toISOString(), startTime);
//         const updatedEndDate = getDateTimeString(endDate.toISOString(), endTime);

//         if (DateTime.fromJSDate(new Date(updatedStartDate)) < DateTime.now()) {
//             showToast("Start time cannot be in the past", { type: "warning" });
//             return;
//         }

//         setIsLoading(true);

//         const updatedBooking: Partial<BookingsData> = {
//             bookingId,
//             bookingType,
//             startTime: updatedStartDate,
//             endTime: updatedEndDate,
//             // valetLocation: bookingType === "VALET" ? valetLocation : undefined,
//         };

//         if (bookingType === "VALET") {
//             singleBookingMutation.mutate(updatedBooking);
//         } else if (bookingType === "Multi") {
//             multiBookingMutation.mutate(updatedBooking);
//         } else {
//             singleBookingMutation.mutate(updatedBooking);
//         }
//     };

//     return (
//         <div className="scrollable-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
//             <div>
//                 <Text as="h3">Booking Type</Text>
//                 <div className="flex flex-row space-x-4 mt-6">
//                 <Button
//     onClick={() => setBookingType("Single")}
//     variant={bookingType === "Single" ? "default" : "secondary"}
//     className={`w-44 ${bookingType === "Single" ? "text-white" : "text-primaryText"}`}
//     style={{ display: bookingType === "Guest" ? "none" : "block" }}
// >
//     Single Booking
// </Button>
// <Button
//     onClick={() => setBookingType("Multi")}
//     variant={bookingType === "Multi" ? "default" : "secondary"}
//     className={`w-44 ${bookingType === "Multi" ? "text-white" : "text-primaryText"}`}
//     style={{ display: bookingType === "Guest" ? "none" : "block" }}
// >
//     Multi Booking
// </Button>
// <Button
//     onClick={() => setBookingType("Guest")}
//     variant={bookingType === "Guest" ? "default" : "secondary"}
//     className={`w-44 ${bookingType === "Guest" ? "text-white" : "text-primaryText"}`}
//     style={{ display: bookingType === "Guest" ? "block" : "none" }}
// >
//     Valet Booking
// </Button>

//                 </div>
//             </div>

//             <Text className="mt-10" as="h3">Booking Date</Text>
//             <div className="mt-6">
//                 {bookingType === "Single" || bookingType === "Guest" ? (
//                     <DatePicker setNewDate={setStartDate} initialDate={startDate} />
//                 ) : (
//                     <DatePickerWithRange setStartDate={setStartDate} setEndDate={setEndDate} startDate={startDate} endDate={endDate} />
//                 )}
//             </div>

//             <div className="mt-4 flex flex-row" style={{ display: bookingType === "Guest" ? "none" : "block" }}>
//                 <div className="flex flex-col">
//                     <Text as="h3">Start Time</Text>
//                     <TimePicker initialTime={startTime} onChange={updateStartTime} />
//                 </div>
//                 <div className="flex flex-col ml-6">
//                     <Text as="h3">End Time</Text>
//                     <TimePicker initialTime={endTime} onChange={updateEndTime} />
//                 </div>
//             </div>

//             {bookingType === "Guest" && (
//               <div className="mt-8">
//               <Text as="h3">Retail User Details</Text>
//               <div className="flex flex-col gap-4 mt-6">
//               <input
//                               className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
//                               type="text"
//                               placeholder="Name*"
//                               value={retailUserName}
//                               onChange={(e) => setRetailUserName(e.target.value)}
//                               required
//                           />
//                           <input
//                               className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
//                               type="text"
//                               placeholder="Email*"
//                               value={retailUserEmail}
//                               onChange={(e) => setRetailUserEmail(e.target.value)}
//                               required
//                           />
//                            <input
//                               className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
//                               type="text"
//                               placeholder="Phone*"
//                               value={retailUserPhone}
//                               onChange={(e) => setRetailUserPhone(e.target.value)}
//                               required
//                           />
//                            <input
//                               className="date-picker mb-4 px-4 border-input items-center justify-center ring-offset-background focus:outline-blue-500 placeholder:text-muted-foreground flex h-12 w-64 rounded-md border hover:border-2 disabled:opacity-50 border-gray-400"
//                               type="text"
//                               placeholder="Plate"
//                               value={retailUserPlate}
//                             //   onChange={(e) => setRetailUserPlate(e.target.value.toUpperCase())}
//                           />
//               </div>

//           </div>
//             )}

//             <div className="absolute bottom-0 mx-12 right-0 mb-20 flex flex-row justify-end">
//                 <SheetClose asChild>
//                     <Button variant="secondary" className="w-44 text-primaryText">
//                         Cancel
//                     </Button>
//                 </SheetClose>
//                 <Button variant="default" isLoading={isLoading} onClick={handleBookingUpdate} className="w-44 ml-4 text-white">
//                     Save Changes
//                 </Button>
//             </div>
//         </div>
//     );
// };

// export default EditBookingBody;
